import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useNonProfitProviderConfirmSchema } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/components/NonProfitProviders/components/NonProfitProviderRow/components/NonProfitProviderForm/components/NonProfitProviderFormConfirmModal/useNonProfitProviderConfirmSchema";
import { Button } from "@components/Button";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Link } from "@components/Link/Link";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useAddNonProfitProviderMutation } from "@services/nonProfits/mutations";
import type { INonProfitProvider } from "@services/nonProfits/types";
import type { IOverviewJointBillService } from "@services/overview/types";
import { isNullOrUndefined } from "@utils/common";

interface NonProfitProviderFormConfirmModal extends IUseModalProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
  amount: number;
  provider: INonProfitProvider;
}

interface IFormData {
  personalCode: string;
}

export const NonProfitProviderFormConfirmModal: React.FC<
  NonProfitProviderFormConfirmModal
> = (props) => {
  const {
    jointBillServiceId,
    periodId,
    provider: { serviceProviderId, privacyPolicyUrl, isPersonalCodeRequired },
    amount,
    ...rest
  } = props;
  const { t } = useTranslation();
  const modal = useModal(rest);
  const schema = useNonProfitProviderConfirmSchema();

  const { handleSubmit, control, setError } = useForm<IFormData>({
    defaultValues: {
      personalCode: "",
    },
    resolver: isPersonalCodeRequired ? zodResolver(schema) : undefined,
  });
  const { setApiErrors } = useApiErrorUtils(setError);

  const { mutateAsync: addNonProfitServiceProvider, isPending } =
    useAddNonProfitProviderMutation({
      jointBillServiceId,
      serviceProviderId,
      periodId,
    });

  const addNonProfitProvider = async (data: IFormData) => {
    const result = await addNonProfitServiceProvider({
      amount,
      personalCode: data.personalCode,
    }).catch(setApiErrors);

    if (!isNullOrUndefined(result)) {
      toast.success(t("nonProfitProviders.confirmModal.toast.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      title={t("nonProfitProviders.confirmModal.title")}
      subtitle={t("nonProfitProviders.confirmModal.description")}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("nonProfitProviders.confirmModal.actions.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(addNonProfitProvider)}
            isLoading={isPending}
          >
            {t("nonProfitProviders.confirmModal.actions.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-6">
        {isPersonalCodeRequired && (
          <FormInput
            label={t(
              "nonProfitProviders.confirmModal.form.fields.personalCode.label",
            )}
            name="personalCode"
            control={control}
          />
        )}
        {privacyPolicyUrl && (
          <p className="text-sm font-normal text-grey-dark">
            {t("nonProfitProviders.confirmModal.privacyPolicyDescription")}{" "}
            <Link to={privacyPolicyUrl} target="_blank">
              {privacyPolicyUrl}
            </Link>
          </p>
        )}
      </div>
    </Modal>
  );
};
